/**
 * v1 Allowed Domains Page
 * Manage email domain restrictions for SaifChat registration
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { v1AllowedDomainsApi, AllowedDomain } from '@/features/allowed_domains/api'
import { DataTable, ColumnDef } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Shield, Plus, RefreshCw, Trash2, Power, PowerOff, AlertCircle, Edit } from 'lucide-react'
import { CreateAllowedDomainDialog } from '@/features/allowed_domains/components/CreateAllowedDomainDialog'
import { EditAllowedDomainDialog } from '@/features/allowed_domains/components/EditAllowedDomainDialog'
import { DeleteAllowedDomainDialog } from '@/features/allowed_domains/components/DeleteAllowedDomainDialog'

export default function AllowedDomainsPage() {
  const queryClient = useQueryClient()

  // Fetch policies
  const { data: policies, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['allowedDomains'],
    queryFn: () => v1AllowedDomainsApi.getAllowedDomains(),
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => {
      if (!id) {
        throw new Error('Policy ID is required')
      }
      return v1AllowedDomainsApi.updateAllowedDomain(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowedDomains'] })
      toast.success('Policy updated successfully')
    },
    onError: (err: any) => {
      toast.error(`Failed to update policy: ${err.message || 'Unknown error'}`)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => v1AllowedDomainsApi.deleteAllowedDomain(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowedDomains'] })
      toast.success('Policy deleted successfully')
      setDeleteDialog(null)
    },
    onError: () => {
      toast.error('Failed to delete policy')
    },
  })

  // Modal states
  const [createDialog, setCreateDialog] = useState(false)
  const [editDialog, setEditDialog] = useState<AllowedDomain | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<string | null>(null)

  const handleRefresh = () => {
    refetch()
  }

  const handleToggle = (policy: AllowedDomain) => {
    if (!policy._id) {
      toast.error('Policy ID is missing. Please refresh the page.')
      console.error('Policy object:', policy)
      return
    }

    updateMutation.mutate({
      id: String(policy._id),
      data: {
        name: policy.name,
        allowedDomains: policy.allowedDomains,
        isActive: !policy.isActive,
      },
    })
  }

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id)
  }

  // Define table columns
  const columns: ColumnDef<AllowedDomain>[] = [
    {
      key: 'name',
      header: 'Policy Name',
      cell: (policy) => (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{policy.name}</div>
            <div className="text-sm text-muted-foreground">
              {policy.allowedDomains.length} domain{policy.allowedDomains.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'domains',
      header: 'Allowed Domains',
      cell: (policy) => (
        <div className="flex flex-wrap gap-1">
          {policy.allowedDomains.slice(0, 3).map((domain, idx) => (
            <Badge key={idx} variant="outline" className="font-mono text-xs">
              {domain}
            </Badge>
          ))}
          {policy.allowedDomains.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{policy.allowedDomains.length - 3} more
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (policy) => (
        <Badge variant={policy.isActive ? 'default' : 'secondary'}>
          {policy.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      cell: (policy) => new Date(policy.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (policy) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (!policy._id) {
                toast.error('Policy ID is missing. Please refresh the page.')
                return
              }
              setEditDialog(policy)
            }}
            disabled={updateMutation.isPending}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <Button
            size="sm"
            variant={policy.isActive ? 'outline' : 'default'}
            onClick={() => handleToggle(policy)}
            disabled={updateMutation.isPending}
          >
            {policy.isActive ? (
              <>
                <PowerOff className="mr-2 h-4 w-4" />
                Deactivate
              </>
            ) : (
              <>
                <Power className="mr-2 h-4 w-4" />
                Activate
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setDeleteDialog(String(policy._id || ''))}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ]

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load allowed domain policies. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Allowed Domains</h1>
          <p className="text-muted-foreground mt-2">
            Manage email domain restrictions for SaifChat user registration
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Policy
          </Button>
        </div>
      </div>

      {/* Policies Table */}
      <Card>
        <CardHeader>
          <CardTitle>Domain Policies</CardTitle>
          <CardDescription>
            {policies?.length || 0} policy{policies?.length !== 1 ? 'ies' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : !policies || policies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <p>No domain policies found</p>
              <p className="text-sm">Create your first policy to get started</p>
            </div>
          ) : (
            <DataTable
              data={policies}
              columns={columns}
              isLoading={isLoading}
            />
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <CreateAllowedDomainDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
      />

      {/* Edit Dialog */}
      <EditAllowedDomainDialog
        open={!!editDialog}
        policy={editDialog}
        onClose={() => setEditDialog(null)}
      />

      {/* Delete Dialog */}
      <DeleteAllowedDomainDialog
        open={!!deleteDialog}
        policyId={deleteDialog}
        onClose={() => setDeleteDialog(null)}
        onConfirm={handleDelete}
      />
    </div>
  )
}